import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Users } from "lucide-react";
import {
  useGetGroupMembersQuery,
  useGetMembersQuery,
} from "../../infrastructure/api/membersGroupsApi";
import type { Group, Member } from "../../domain/entities/Member";
import { useGetRolesQuery } from "@/features/roles-permissions/infrastructure/api/RolesApi";
import { ViewMemberDialog } from "./ViewMemberDialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
  canViewMember?: boolean;
}

export const ViewGroupMembersDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  group,
  canViewMember = true,
}) => {
  const { t } = useTranslation("membersGroups");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const { data: members = [], isLoading } = useGetGroupMembersQuery(
    { groupId: group?.id ?? "" },
    { skip: !open || !group, refetchOnMountOrArgChange: true },
  );
  const { data: roles = [] } = useGetRolesQuery(undefined, { skip: !open });
  const { data: instituteMembers = [] } = useGetMembersQuery(
    { pageSize: 100 },
    { skip: !open },
  );

  const roleNameById = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles],
  );
  const instituteMemberById = useMemo(
    () => new Map(instituteMembers.map((m) => [m.id, m])),
    [instituteMembers],
  );

  const getRoleLabel = (member: Member) => {
    if (member.role) return member.role;
    if (member.role_id && roleNameById.get(member.role_id)) {
      return roleNameById.get(member.role_id)!;
    }
    const instituteMember = instituteMemberById.get(member.id);
    if (instituteMember?.role) return instituteMember.role;
    if (instituteMember?.role_id && roleNameById.get(instituteMember.role_id)) {
      return roleNameById.get(instituteMember.role_id)!;
    }
    return t("viewGroupMembersDialog.roleNotAssigned");
  };

  const getMemberForView = (member: Member): Member => {
    const instituteMember = instituteMemberById.get(member.id);
    if (instituteMember) return instituteMember;

    return {
      ...member,
      role: getRoleLabel(member),
      phone: member.phone || "",
      role_id: member.role_id || "",
      designation_id: member.designation_id || "",
      status: member.status || "Active",
      createdAt: member.createdAt || "",
    };
  };

  const handleMemberClick = (member: Member) => {
    if (!canViewMember) return;
    setSelectedMember(getMemberForView(member));
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-h-[80vh] sm:max-w-[520px]">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="text-lg font-semibold">
            {group?.name ?? t("viewGroupMembersDialog.defaultGroupName")} - {t("viewGroupMembersDialog.titleSuffix")}
          </DialogTitle>
          {!isLoading && (
            <p className="text-sm text-gray-500">
              {t("viewGroupMembersDialog.members", { count: members.length })}
            </p>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
              <Users className="w-8 h-8" />
              <p className="text-sm">{t("viewGroupMembersDialog.empty")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => handleMemberClick(member)}
                  disabled={!canViewMember}
                  className={`flex w-full items-center gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left transition-colors ${
                    canViewMember
                      ? "cursor-pointer hover:bg-gray-100"
                      : "cursor-default"
                  }`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {member.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className="truncate text-sm font-medium">
                        {member.name}
                      </p>
                      <span className="shrink-0 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
                        {getRoleLabel(member)}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {member.email}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <ViewMemberDialog
      open={!!selectedMember}
      onOpenChange={(val) => {
        if (!val) setSelectedMember(null);
      }}
      member={selectedMember}
    />
    </>
  );
};
